import LoadingIndicator from "@/components/LoadingIndicator";
import { DateFilterValue } from "@/components/modal-invoice";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import DataTable from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { importStatus } from "@/constants/invoiceTypes";
import { useImport, useRevertImport } from "@/hooks/useImport";
import { UploadList } from "@/types/uploadInvoice";
import { getInvoiceTypeDescription } from "@/utils/helper/invoice.mapping.helper";
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  Row,
  RowSelectionState,
} from "@tanstack/react-table";
import { endOfDay, format, startOfDay } from "date-fns";
import { Eye, Loader2, RotateCcw, Search } from "lucide-react";
import { ChangeEvent, useState } from "react";
import { navigate } from "wouter/use-browser-location";

const RevertUploadAction = ({ upload }: { upload: UploadList }) => {
  const { revertImport, isReverting } = useRevertImport();
  const [open, setOpen] = useState(false);

  if (upload.status !== "Completed") return null;

  if (upload.revertedAt) {
    return (
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        Reverted
      </span>
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-red-600 hover:text-red-700"
          aria-label={`Revert import ${upload.fileName}`}
        >
          <RotateCcw className="h-4 w-4 mr-1" />
          Revert
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Revert this import?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete every invoice imported from{" "}
            <span className="font-medium">&ldquo;{upload.fileName}&rdquo;</span>
            . Deleted invoices cannot be recovered. Invoices already submitted
            to LHDN or consolidated into another invoice will be left alone and
            skipped — everything else in this import will be deleted.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={isReverting}
            onClick={() => revertImport(upload.id)}
            className="bg-red-600 hover:bg-red-700"
          >
            {isReverting ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Reverting
              </>
            ) : (
              "Revert Import"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const getColumns = (
  onRowAction: (id: string, status: string) => void,
): ColumnDef<UploadList>[] => [
  {
    id: "rowNumber",
    header: () => (
      <span className="w-full flex justify-center font-semibold">No.</span>
    ),
    cell: ({ row, table }) => {
      const { pageIndex, pageSize } = table.getState().pagination;

      const pageRows = table.getPaginationRowModel().rows;

      const rowIndexInPage = pageRows.findIndex(
        (pageRow) => pageRow.id === row.id,
      );

      const rowNumber = pageIndex * pageSize + rowIndexInPage + 1;

      return (
        <span className="w-full flex items-center justify-center font-semibold">
          {rowNumber}
        </span>
      );
    },
  },
  {
    accessorKey: "fileName",
    header: "File Name",
  },
  {
    accessorKey: "typeCode",
    header: "Upload Type",
    cell: ({ row }) => {
      return getInvoiceTypeDescription(row.original.typeCode);
    },
  },
  {
    accessorKey: "createdAt",
    header: "Date Imported",
    cell: ({ row }) => {
      const date = row.original.createdAt;
      try {
        return format(new Date(date), "dd/MM/yyyy");
      } catch {
        return date;
      }
    },
    filterFn: (
      row: Row<UploadList>,
      columnId: string,
      filterValue: { fromDate: string; toDate: string },
    ) => {
      const importDate = row.getValue(columnId) as Date;
      const { fromDate, toDate } = filterValue;

      let matchesDate = true;
      if (fromDate || toDate) {
        // Normalize importDate to start of day for comparison
        const importDateStart = startOfDay(importDate);
        const fromDateObj = fromDate ? startOfDay(new Date(fromDate)) : null;
        const toDateObj = toDate ? endOfDay(new Date(toDate)) : null;

        if (fromDateObj && importDateStart < fromDateObj) {
          matchesDate = false;
        }
        if (toDateObj && importDateStart > toDateObj) {
          matchesDate = false;
        }
      }
      return matchesDate;
    },
  },
  {
    accessorKey: "updatedAt",
    header: "Date Modified",
    cell: ({ row }) => {
      const date = row.original.updatedAt;
      try {
        if (date) {
          return format(new Date(date), "dd/MM/yyyy");
        }
      } catch {
        return date;
      }
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      return (
        <span
          className={`px-2 rounded-md text-white ${
            row.original.status === "Completed"
              ? "bg-green-500"
              : row.original.status === "Failed"
                ? "bg-red-500"
                : "bg-orange-500"
          }`}
        >
          {row.original.status}
        </span>
      );
    },
  },
  {
    id: "select",
    header: "Action",
    cell: ({ row, table }) => (
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            table.setRowSelection({ [row.id]: true });
            onRowAction(row.original.id, row.original.status);
          }}
          aria-label={`View details for ${row.original.id}`}
        >
          <Eye />
        </Button>
        <RevertUploadAction upload={row.original} />
      </div>
    ),
  },
];

const ImportListPage = () => {
  const { importRecords, isLoading } = useImport();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState<string>("");

  const handleRowAction = (id: string, status: string) => {
    navigate(`/import/records/${id}`, { state: { status } });
  };

  const globalFilterFn: FilterFn<UploadList> = (
    row: Row<UploadList>,
    _columnId: string,
    filterValue: string,
  ) => {
    const searchValue = filterValue.toLowerCase();
    const fileName = (row.original.fileName || "").toLowerCase();
    return !searchValue || fileName.includes(searchValue);
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilter(value);
  };

  const handleFromDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fromDate = e.target.value;
    setColumnFilters((prev) => {
      const existing = prev.find((filter) => filter.id === "createdAt");
      const existingValue: DateFilterValue = existing
        ? (existing.value as DateFilterValue)
        : { fromDate: "", toDate: "" };
      return [
        ...prev.filter((filter) => filter.id !== "createdAt"),
        { id: "createdAt", value: { ...existingValue, fromDate } },
      ];
    });
  };

  const handleToDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const toDate = e.target.value;
    setColumnFilters((prev) => {
      const existing = prev.find((filter) => filter.id === "createdAt");
      const existingValue: DateFilterValue = existing
        ? (existing.value as DateFilterValue)
        : { fromDate: "", toDate: "" };
      return [
        ...prev.filter((filter) => filter.id !== "createdAt"),
        { id: "createdAt", value: { ...existingValue, toDate } },
      ];
    });
  };

  const importDateFilter = columnFilters.find((f) => f.id === "createdAt");
  const dateFilterValue = (importDateFilter?.value as DateFilterValue) || {
    fromDate: "",
    toDate: "",
  };
  const statusFilter = columnFilters.find((f) => f.id === "status");
  const statusFilterValue = (statusFilter?.value as string) || "";

  const handleStatusChange = (selectedStatus: string) => {
    setColumnFilters((prev) => {
      return [
        ...prev.filter((filter) => filter.id !== "status"),
        ...(selectedStatus !== "All"
          ? [{ id: "status", value: selectedStatus }]
          : []),
      ];
    });
  };
  const maxLabelLength = Math.max(
    ...importStatus.map((status) => status.label.length),
  );
  const minWidth = Math.max(100, maxLabelLength * 8 + 60);
  return (
    <div className="h-full w-full space-y-4 px-4">
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <UplaodListHeader />
          <div className="w-full relative flex flex-col md:flex-row justify-start gap-4">
            <div className="w-full md:w-1/3">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder="File name"
                value={globalFilter}
                onChange={handleSearch}
                className="pl-8"
                aria-label="Search file name"
              />
            </div>
            <div className="flex gap-1.5 items-center">
              <Label>From</Label>
              <Input
                type="date"
                value={dateFilterValue.fromDate}
                onChange={handleFromDateChange}
                aria-label="From date"
              />
            </div>
            <div className="flex gap-1.5 items-center">
              <Label>To</Label>
              <Input
                type="date"
                value={dateFilterValue.toDate}
                onChange={handleToDateChange}
                aria-label="To date"
              />
            </div>
            <div className="min-w-fit flex gap-1.5 items-center">
              <Label>Status</Label>
              <Select
                onValueChange={handleStatusChange}
                value={statusFilterValue}
              >
                <SelectTrigger style={{ minWidth: `${minWidth}px` }}>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  {importStatus.map((status) => (
                    <SelectItem key={status.label} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DataTable<UploadList>
            data={importRecords || []}
            columns={getColumns(handleRowAction)}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            globalFilter={globalFilter}
            globalFilterFn={globalFilterFn}
            setGlobalFilter={setGlobalFilter}
            showSelectedIndicator={false}
          />
        </>
      )}
    </div>
  );
};

const UplaodListHeader = () => {
  return (
    <div className="w-full flex flex-col gap-2.5 bg-slate-100 border border-slate-200 text-slate-600 p-3 md:p-4 rounded-md select-none leading-none text-sm">
      <span className="font-bold leading-none">Note:</span>
      <p>
        1. All invoices imported in a file with the status Completed are
        automatically stored and can be view in dashboard.
      </p>
      <p>
        2. A record with status Completed is no longer editable. It is advisable
        to edit any invoices that require attention in the invoice tab.
      </p>
      <p>
        3. User may amend any records with the status Failed numerous time until
        it achieved the status Completed.
      </p>
    </div>
  );
};

export default ImportListPage;
